import type { Ring, Vec2 } from '../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { orientedFace, planPoint } from '../solids';
export interface ConventWall {
  readonly from: readonly number[];
  readonly to: readonly number[];
  readonly width: number;
  readonly height: number;
}

function wallFrame(w: ConventWall) {
  const a: Vec2 = [w.from[0]!, w.from[1]!],
    b: Vec2 = [w.to[0]!, w.to[1]!],
    length = Math.hypot(b[0] - a[0], b[1] - a[1]),
    du = (b[0] - a[0]) / length,
    dv = (b[1] - a[1]) / length;
  const point = (t: number, side: number): Vec2 => [
    a[0] + (b[0] - a[0]) * t - (dv * w.width * side) / 2,
    a[1] + (b[1] - a[1]) * t + (du * w.width * side) / 2,
  ];
  return { length, du, dv, point };
}

export function conventEnclosureRings(walls: readonly ConventWall[]): Ring[] {
  return walls.map((w) => {
    const { point } = wallFrame(w);
    return [point(0, -1), point(1, -1), point(1, 1), point(0, 1)];
  });
}

/** Closed masonry strips follow the local ground without lifting the whole precinct. */
export function buildConventEnclosure(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  terrain: (p: Vec2) => number,
  walls: readonly ConventWall[]
): void {
  for (const w of walls) {
    const { length, du, dv, point } = wallFrame(w),
      count = Math.ceil(length / 2),
      fitted = { ...style, extent: [length, w.height] as const };
    const at = (t: number, side: number, top: boolean) => {
      const p = point(t, side),
        base = terrain(point(t, 0));
      return planPoint(p[0], base + (top ? w.height : -0.03), p[1]);
    };
    for (let k = 0; k < count; k++) {
      const a = k / count,
        b = (k + 1) / count;
      for (const side of [-1, 1])
        orientedFace(
          builder,
          fitted,
          frame,
          [at(a, side, false), at(b, side, false), at(b, side, true), at(a, side, true)],
          [-dv * side, 0, -du * side]
        );
      for (const top of [false, true])
        orientedFace(
          builder,
          fitted,
          frame,
          [at(a, -1, top), at(b, -1, top), at(b, 1, top), at(a, 1, top)],
          [0, top ? 1 : -1, 0]
        );
    }
    for (const t of [0, 1])
      orientedFace(
        builder,
        fitted,
        frame,
        [at(t, -1, false), at(t, 1, false), at(t, 1, true), at(t, -1, true)],
        [du * (t ? 1 : -1), 0, -dv * (t ? 1 : -1)]
      );
  }
}
